'use client';

import { InventoryContext } from '@/contexts/InventoryContext';
import { NewInventoryFiltersContext } from '@/contexts/NewInventoryFiltersContext';
import { useCustomSearchParams } from '@/hooks/useCustomSearchParams';
import { IComponentGridFavoritesExtraSection } from '@/types/cms';
import { FilterSection } from '@/types/inventory';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'next/link';
import { PropsWithChildren, useContext, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { InventorySlider } from '../Inventory/InventorySlider';
import {
  GridFavoritesContext,
  GridFavoritesContextData,
  GridFavoritesFilters,
  GridFavoritesSort,
} from './GridFavoritesContext';

interface SecondarySectionProps {
  title: string;
  description: string;
  extraHeader?: JSX.Element;
}

export const SecondarySection = ({
  description,
  title,
  children,
  extraHeader,
}: PropsWithChildren<SecondarySectionProps>) => (
  <section className="mt-12 md:mt-20" data-testid={`${title.split(/\s+/).slice(0, 2).join('-').toLowerCase()}-section`}>
    <div className="flex justify-between items-center md:items-end mb-9">
      <div className="flex flex-col">
        <h2 className="font-bold text-lg md:text-[1.75rem]">{title}</h2>
        <p className="mt-3.5 text-[#3F585E] max-md:hidden">{description}</p>
      </div>
      {extraHeader}
    </div>
    {children}
  </section>
);

const ViewAllLink = ({ href }: { href: string }) => (
  <Link prefetch href={href} className="flex items-center gap-1 md:gap-2 mr-2 md:mr-3.5">
    <span className="font-bold text-xs md:text-base">View All</span>
    <FontAwesomeIcon icon={faChevronRight} className="size-2.5 md:size-4" />
  </Link>
);

interface GridFavoritesLayoutProps {
  className?: string;
  extraSections: IComponentGridFavoritesExtraSection[];
}

export const GridFavoritesLayout = ({
  children,
  className,
  extraSections,
}: PropsWithChildren<GridFavoritesLayoutProps>) => {
  const { context } = useContext(InventoryContext);
  const hasItems = !!context.inventoryFavorites?.length;
  const { getSectionOptions } = useContext(NewInventoryFiltersContext);

  const [filters, setFilters] = useState<GridFavoritesFilters>({ body: null });
  const [sort, setSort] = useState<GridFavoritesSort>(GridFavoritesSort.RECENTLY_ADDED);
  const { setSearchParam } = useCustomSearchParams();

  const contextData = useMemo(
    (): GridFavoritesContextData => ({
      filters,
      sort,
    }),
    [filters, sort],
  );

  return (
    <GridFavoritesContext.Provider value={contextData}>
      <div className={twMerge('relative mt-14 md:mt-20 mb-[7.5rem] font-inter text-[#0F0603]', className)}>
        <div className="mb-6 flex items-end justify-between">
          <h1 className="font-bold text-xl md:text-[1.75rem]">My Favorites</h1>
          {hasItems && (
            <div className="flex gap-1.5 md:gap-4">
              <select
                className="border border-[#75787B] rounded px-1.5 md:py-2 md:px-4 pr-6 md:pr-9 text-xs md:text-base"
                value={filters.body || ''}
                onChange={(e) => {
                  setSearchParam('page', '1');
                  setFilters({ ...filters, body: e.target.value || null });
                }}
              >
                <option value="">All Types</option>
                {getSectionOptions(FilterSection.RV_TYPE).map((item, index) => {
                  if (typeof item !== 'string') return null;

                  return (
                    <option key={item + index} value={item}>
                      {item}
                    </option>
                  );
                })}
              </select>
              <select
                className="border border-[#75787B] rounded py-0.5 px-1.5 md:py-2 md:px-4 pr-6 md:pr-9 text-xs md:text-base"
                value={sort}
                onChange={(e) => {
                  setSearchParam('page', '1');
                  setSort(e.target.value as GridFavoritesSort);
                }}
              >
                <option value={GridFavoritesSort.RECENTLY_ADDED}>Sort By: Recent</option>
                <option value={GridFavoritesSort.PRICE_ASC}>Price Asc</option>
                <option value={GridFavoritesSort.PRICE_DESC}>Price Desc</option>
              </select>
            </div>
          )}
        </div>
        {children}

        {extraSections.map((section, index) => (
          <SecondarySection
            key={section.title + index}
            title={section.title}
            description={section.description}
            extraHeader={section.view_all_link ? <ViewAllLink href={section.view_all_link} /> : null}
          >
            <InventorySlider filters={section.filters || ''} />
          </SecondarySection>
        ))}
      </div>
    </GridFavoritesContext.Provider>
  );
};
